import React, { useState, useEffect, useRef } from 'react';
import { useOrganization, useUser } from '@clerk/clerk-react';
import type { Stripe } from '@stripe/stripe-js';
import { correlationId } from '@interlucent/ids';
import { useStore } from '../store';
import { ConfirmDialog } from './ConfirmDialog';
import { StripeCheckout } from './StripeCheckout';
import { useFocusTrap } from '../utils/focusTrap';
import { send, waitForReady } from '../ws';
import { waitForAck } from '../utils/waitForAck';
import type { PlanTier } from '@interlucent/organization-protocol';
import { logger } from '../log';
import styles from './SettingsView.module.css';

const isSubscriptionsEnabled = import.meta.env.VITE_FEATURE_SUBSCRIPTIONS_ENABLED === 'true';

const log = logger('settings');

// Lazy load Stripe only when needed
let stripePromise: Promise<Stripe | null> | null = null;
function getStripe() {
  if (!stripePromise) {
    // Dynamic import to avoid loading Stripe SDK on initial page load
    stripePromise = import('@stripe/stripe-js').then(({ loadStripe }) =>
      loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '')
    );
  }
  return stripePromise;
}

type SelectPlanResponse = {
  ok: boolean;
  progress: any;
  requiresPayment: boolean;
  clientSecret?: string;
  subscriptionId?: string;
};

type SettingsTab = 'members' | 'organization' | 'subscription';

interface SettingsViewProps {
  initialTab?: SettingsTab;
}

export const SettingsView: React.FC<SettingsViewProps> = ({ initialTab = 'members' }) => {
  const { organization, membership, memberships, invitations } = useOrganization({
    memberships: { pageSize: 100 },
    invitations: { pageSize: 100 }
  });
  const { user } = useUser();
  const orgData = useStore(s => s.organization);

  const inviteModalRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    initialTab === 'subscription' && !isSubscriptionsEnabled ? 'members' : initialTab
  );
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'org:member' | 'org:admin'>('org:member');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Inline plan change state
  const [plans, setPlans] = useState<PlanTier[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [planError, setPlanError] = useState('');
  const [changingPlan, setChangingPlan] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);

  // Enable focus trap for dialogs
  useFocusTrap(inviteModalRef, showInviteModal);

  // Load plans when subscription tab is active
  useEffect(() => {
    if (activeTab === 'subscription' && plans.length === 0) {
      loadPlans();
    }
  }, [activeTab]);

  const loadPlans = async () => {
    setLoadingPlans(true);
    setPlanError('');

    try {
      await waitForReady();
      const corr = correlationId();
      send('onboarding.getPlans', {}, corr);
      const data = await waitForAck<{ plans: PlanTier[] }>(corr);
      setPlans(data.plans || []);
      // Pre-select current plan
      if (orgData?.planTierId) {
        setSelectedPlanId(orgData.planTierId);
      }
    } catch (err: any) {
      log.error('failed to load plans', err);
      setPlanError('Failed to load available plans');
    } finally {
      setLoadingPlans(false);
    }
  };

  const cancelPlanChange = () => {
    setClientSecret(null);
    setSubscriptionId(null);
    setSelectedPlanId(orgData?.planTierId || '');
    setPlanError('');
  };

  const handleChangePlan = async () => {
    if (!selectedPlanId || selectedPlanId === orgData?.planTierId) {
      return;
    }

    if (!organization) {
      setPlanError('No organization found');
      return;
    }

    const plan = plans.find(p => p.id === selectedPlanId);
    if (!plan) {
      setPlanError('Selected plan not found');
      return;
    }

    // Enterprise plan - show contact message
    if (plan.tierSlug === 'enterprise') {
      setPlanError('Please contact sales@interlucent.ai for Enterprise plans');
      return;
    }

    setChangingPlan(true);
    setPlanError('');

    try {
      await waitForReady();
      const returnUrl = `${window.location.origin}/settings/subscription`;
      const corr = correlationId();
      send('onboarding.selectPlan', {
        planId: selectedPlanId,
        orgId: organization.id,
        orgName: organization.name || 'Organization',
        returnUrl
      }, corr);

      const response = await waitForAck<SelectPlanResponse>(corr);

      if (response.requiresPayment && response.clientSecret && response.subscriptionId) {
        // Paid plan - show embedded Stripe checkout
        setClientSecret(response.clientSecret);
        setSubscriptionId(response.subscriptionId);
      } else {
        // Free plan or downgrade - complete immediately
        setSuccess('Plan changed successfully');
        cancelPlanChange();
      }
    } catch (err: any) {
      log.error('failed to change plan', err);
      setPlanError(err.message || 'Failed to change plan');
    } finally {
      setChangingPlan(false);
    }
  };

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const isAdmin = membership?.role === 'org:admin';

  // Handle escape key for Invite Member dialog
  useEffect(() => {
    if (showInviteModal) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          setShowInviteModal(false);
          setInviteEmail('');
          setError('');
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [showInviteModal]);

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !organization) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await organization.inviteMember({
        emailAddress: inviteEmail.trim(),
        role: inviteRole
      });

      setSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setShowInviteModal(false);

      // Reload invitations
      await invitations?.revalidate();
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Revoke Invitation',
      message: 'Revoke this invitation?',
      onConfirm: async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));

        try {
          const invitationToRevoke = invitations?.data?.find(i => i.id === invitationId);
          if (invitationToRevoke) {
            await invitationToRevoke.revoke();
            setSuccess('Invitation revoked');
            await invitations?.revalidate();
          } else {
            setError('Invitation not found');
          }
        } catch (err: any) {
          log.error('error revoking invitation', err);
          setError(err.errors?.[0]?.message || err.message || 'Failed to revoke invitation');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleRemoveMember = async (userId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Member',
      message: 'Remove this member from the organization?',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));

        try {
          const memberToRemove = memberships?.data?.find(m => m.publicUserData.userId === userId);
          if (memberToRemove) {
            await memberToRemove.destroy();
            setSuccess('Member removed');
            await memberships?.revalidate();
          }
        } catch (err: any) {
          setError(err.errors?.[0]?.message || 'Failed to remove member');
        }
      },
    });
  };

  const handleUpdateRole = async (userId: string, newRole: 'org:member' | 'org:admin') => {
    try {
      const memberToUpdate = memberships?.data?.find(m => m.publicUserData.userId === userId);
      if (memberToUpdate) {
        await memberToUpdate.update({ role: newRole });
        setSuccess('Role updated');
        await memberships?.revalidate();
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'Failed to update role');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Settings</h1>
        <p className={styles.description}>
          Manage organization members, invitations, and settings
        </p>
      </div>

      {error && (
        <div className={`${styles.message} ${styles.errorMessage}`}>
          <span>{error}</span>
          <button className={styles.messageClose} onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className={`${styles.message} ${styles.successMessage}`}>
          <span>{success}</span>
          <button className={styles.messageClose} onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Tabs */}
      <div className={styles.tabs}>
        <ul className={styles.tabList}>
          <li
            className={`${styles.tab} ${activeTab === 'members' ? styles.active : ''}`}
            onClick={() => setActiveTab('members')}
          >
            Members
          </li>
          <li
            className={`${styles.tab} ${activeTab === 'organization' ? styles.active : ''}`}
            onClick={() => setActiveTab('organization')}
          >
            Organization
          </li>
          {isSubscriptionsEnabled && (
            <li
              className={`${styles.tab} ${activeTab === 'subscription' ? styles.active : ''}`}
              onClick={() => setActiveTab('subscription')}
            >
              Subscription
            </li>
          )}
        </ul>
      </div>

      {/* Members Tab */}
      {activeTab === 'members' && (
        <div>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Members ({memberships?.count || 0})</h2>
            {isAdmin && (
              <button
                className={styles.button}
                onClick={() => setShowInviteModal(true)}
              >
                Invite Member
              </button>
            )}
          </div>

          <div className={styles.memberList}>
            {memberships?.data?.map((member) => (
              <div key={member.id} className={styles.memberItem}>
                <div className={styles.memberInfo}>
                  <div className={styles.memberName}>
                    {member.publicUserData.identifier}
                    {member.publicUserData.userId === user?.id && ' (You)'}
                  </div>
                  <div className={styles.memberEmail}>
                    {member.publicUserData.firstName} {member.publicUserData.lastName}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                  <span className={styles.memberRole}>
                    {member.role === 'org:admin' ? 'Admin' : 'Member'}
                  </span>
                  {isAdmin && member.publicUserData.userId !== user?.id && (
                    <div className={styles.memberActions}>
                      <button
                        className={styles.iconButton}
                        onClick={() => handleUpdateRole(
                          member.publicUserData.userId!,
                          member.role === 'org:admin' ? 'org:member' : 'org:admin'
                        )}
                        title={member.role === 'org:admin' ? 'Demote to member' : 'Promote to admin'}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M8 4V12M12 8H4" strokeLinecap="square" />
                        </svg>
                      </button>
                      <button
                        className={`${styles.iconButton} ${styles.deleteButton}`}
                        onClick={() => handleRemoveMember(member.publicUserData.userId!)}
                        title="Remove member"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M3 4H13M5 4V3C5 2.44772 5.44772 2 6 2H10C10.5523 2 11 2.44772 11 3V4M6.5 7.5V11.5M9.5 7.5V11.5M4 4H12V13C12 13.5523 11.5523 14 11 14H5C4.44772 14 4 13.5523 4 13V4Z" strokeLinecap="square" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pending Invites */}
          {isAdmin && invitations?.data && invitations.data.length > 0 && (
            <div className={styles.pendingSection}>
              <h3 className={styles.sectionTitle}>Pending Invitations ({invitations.count})</h3>
              {invitations.data.map((invite) => (
                <div key={invite.id} className={styles.inviteItem}>
                  <div>
                    <div className={styles.inviteEmail}>{invite.emailAddress}</div>
                    <div className={styles.inviteStatus}>
                      {invite.status} • {invite.role === 'org:admin' ? 'Admin' : 'Member'}
                    </div>
                  </div>
                  <button
                    className={`${styles.iconButton} ${styles.deleteButton}`}
                    onClick={() => handleRevokeInvitation(invite.id)}
                    title="Revoke invitation"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M2 2L14 14M14 2L2 14" strokeLinecap="square" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Organization Tab */}
      {activeTab === 'organization' && (
        <div>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Organization Details</h2>
          </div>
          <div className={styles.memberList}>
            <div className={styles.memberItem}>
              <div className={styles.memberInfo}>
                <div className={styles.memberName}>Name</div>
                <div className={styles.memberEmail}>{organization?.name}</div>
              </div>
            </div>
            <div className={styles.memberItem}>
              <div className={styles.memberInfo}>
                <div className={styles.memberName}>ID</div>
                <div className={styles.memberEmail}>{organization?.id}</div>
              </div>
            </div>
            <div className={styles.memberItem}>
              <div className={styles.memberInfo}>
                <div className={styles.memberName}>Created</div>
                <div className={styles.memberEmail}>
                  {organization?.createdAt ? new Date(organization.createdAt).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Tab */}
      {isSubscriptionsEnabled && activeTab === 'subscription' && (
        <div>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Current Plan</h2>
          </div>

          {orgData?.planTier && (
            <div className={styles.memberList}>
              <div className={styles.memberItem}>
                <div className={styles.memberInfo}>
                  <div className={styles.memberName}>{orgData.planTier.tierName}</div>
                  <div className={styles.memberEmail}>
                    {orgData.planTier.priceMonthlyCents
                      ? `$${(orgData.planTier.priceMonthlyCents / 100).toFixed(0)}/month`
                      : 'Free'}
                    {orgData.planRenewsAt && ` • Renews ${new Date(orgData.planRenewsAt).toLocaleDateString()}`}
                  </div>
                </div>
              </div>
            </div>
          )}

          {isAdmin && (
            <>
              <div className={styles.sectionHeader} style={{ marginTop: 'var(--space-8)' }}>
                <h2 className={styles.sectionTitle}>Change Plan</h2>
              </div>

              {planError && (
                <div className={`${styles.message} ${styles.errorMessage}`}>
                  <span>{planError}</span>
                  <button className={styles.messageClose} onClick={() => setPlanError('')}>×</button>
                </div>
              )}

              {clientSecret && subscriptionId ? (
                (() => {
                  const selectedPlan = plans.find(p => p.id === selectedPlanId);
                  const planName = selectedPlan?.tierName || 'Selected Plan';
                  const priceDisplay = selectedPlan?.priceMonthlyCents
                    ? `$${(selectedPlan.priceMonthlyCents / 100).toFixed(0)}`
                    : '$0';
                  return (
                    <StripeCheckout
                      stripePromise={getStripe()}
                      clientSecret={clientSecret}
                      subscriptionId={subscriptionId}
                      planName={planName}
                      priceDisplay={priceDisplay}
                      onSuccess={async (subId: string) => {
                        // Verify subscription with backend to update organization
                        try {
                          await waitForReady();
                          const corr = correlationId();
                          send('subscription.verify', { subscriptionId: subId }, corr);
                          await waitForAck(corr);
                          setSuccess('Plan upgraded successfully');
                        } catch (err: any) {
                          log.error('failed to verify subscription', err);
                          // Still show success - webhook will update eventually
                          setSuccess('Payment successful. Plan updating...');
                        }
                        setClientSecret(null);
                        setSubscriptionId(null);
                        setSelectedPlanId('');
                        // Reload to get updated plan data
                        window.location.reload();
                      }}
                      onCancel={cancelPlanChange}
                    />
                  );
                })()
              ) : loadingPlans ? (
                <div className={styles.emptyState}>Loading available plans...</div>
              ) : (
                <>
                  <div className={styles.plansGrid}>
                    {plans.map((plan) => {
                      const isCurrentPlan = plan.id === orgData?.planTierId;
                      const isSelected = selectedPlanId === plan.id;
                      return (
                        <div
                          key={plan.id}
                          className={`${styles.planCardCompact} ${isSelected ? styles.selected : ''} ${isCurrentPlan ? styles.currentPlan : ''}`}
                          onClick={() => setSelectedPlanId(plan.id)}
                        >
                          <div className={styles.planCardHeader}>
                            <span className={styles.planCardName}>
                              {plan.tierName}
                              {isCurrentPlan && <span className={styles.planCardBadge}>(Current)</span>}
                            </span>
                            <span className={styles.planCardPrice}>
                              {plan.priceMonthlyCents === null
                                ? (plan.tierSlug === 'enterprise' ? 'Contact' : 'Free')
                                : `$${(plan.priceMonthlyCents / 100).toFixed(0)}`
                              }
                            </span>
                          </div>
                          <ul className={styles.planCardFeatures}>
                            <li>{plan.quotaMaxProjects === -1 ? 'Unlimited' : plan.quotaMaxProjects} projects</li>
                            <li>{plan.quotaMaxAppsPerProject === -1 ? 'Unlimited' : plan.quotaMaxAppsPerProject} apps/project</li>
                            <li>{plan.quotaMaxConcurrentStreams === -1 ? 'Unlimited' : plan.quotaMaxConcurrentStreams} streams</li>
                          </ul>
                        </div>
                      );
                    })}
                  </div>

                  <div className={styles.planActions}>
                    <button
                      className={styles.button}
                      disabled={changingPlan || !selectedPlanId || selectedPlanId === orgData?.planTierId}
                      onClick={handleChangePlan}
                    >
                      {changingPlan ? 'Processing...' : 'Change Plan'}
                    </button>
                    {selectedPlanId && selectedPlanId !== orgData?.planTierId && (
                      <button
                        className={`${styles.button} ${styles.secondaryButton}`}
                        onClick={cancelPlanChange}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => {
            setShowInviteModal(false);
            setInviteEmail('');
            setError('');
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="invite-member-title"
        >
          <div ref={inviteModalRef} className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 id="invite-member-title" className={styles.modalTitle}>Invite Member</h2>
            <form onSubmit={handleInviteMember}>
              <div className={styles.formGroup}>
                <label htmlFor="invite-email-input" className={styles.label}>Email Address</label>
                <input
                  id="invite-email-input"
                  type="email"
                  className={styles.input}
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@example.com"
                  required
                  autoFocus
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="invite-role-select" className={styles.label}>Role</label>
                <select
                  id="invite-role-select"
                  className={styles.select}
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'org:member' | 'org:admin')}
                >
                  <option value="org:member">Member</option>
                  <option value="org:admin">Admin</option>
                </select>
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.secondaryButton}`}
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteEmail('');
                    setError('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.button}
                  disabled={loading || !inviteEmail.trim()}
                >
                  {loading ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};
